import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: '',
  email: '',
  organization: '',
  investorType: '',
  jurisdiction: '',
  ticketSize: '',
  objective: ''
};

const AfricaInvestorModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setErrorMsg('');
    setFormData(initialState);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact/africa-investor`, formData);
      setSubmitted(true);
      setTimeout(resetAndClose, 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="africa-investor-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={resetAndClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a365d] rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-serif text-xl font-bold text-[#1a365d]">Investor Inquiry</h2>
              <p className="text-xs text-gray-500">Berkshire Africa Special Situations</p>
            </div>
          </div>
          <button onClick={resetAndClose} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="africa-investor-close-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12" data-testid="africa-investor-success">
              <div className="w-16 h-16 bg-[#e8f5f3] rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-[#2f9e8e]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a365d] mb-2">Inquiry Received</h3>
              <p className="text-gray-600">Our team will reach out with a detailed briefing pack.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="africa-investor-form">
              <p className="text-sm text-gray-600">
                Indicate interest in anchoring or co-investing alongside the Phase 1 permanent capital base.
              </p>

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{errorMsg}</div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="Jane Doe" data-testid="africa-investor-input-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="jane@office.com" data-testid="africa-investor-input-email" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization *</label>
                <input type="text" name="organization" required value={formData.organization} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                  placeholder="Family office, sovereign, DFI, trading house..." data-testid="africa-investor-input-org" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Investor Type *</label>
                  <select name="investorType" required value={formData.investorType} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    data-testid="africa-investor-input-type">
                    <option value="">Select type</option>
                    <option value="GCC Family Office">GCC Family Office</option>
                    <option value="Sovereign / Quasi-Sovereign">Sovereign / Quasi-Sovereign</option>
                    <option value="DFI">Development Finance Institution</option>
                    <option value="Commodity Trader">Commodity Trader</option>
                    <option value="Japanese Trading House">Japanese Trading House</option>
                    <option value="UHNWI">UHNWI</option>
                    <option value="Multi-Family Office">Multi-Family Office</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Jurisdiction</label>
                  <input type="text" name="jurisdiction" value={formData.jurisdiction} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="UAE, KSA, UK..." data-testid="africa-investor-input-jurisdiction" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Indicative Ticket Size *</label>
                <select name="ticketSize" required value={formData.ticketSize} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                  data-testid="africa-investor-input-ticket">
                  <option value="">Select range</option>
                  <option value="$1M – $5M">$1M – $5M</option>
                  <option value="$5M – $15M">$5M – $15M</option>
                  <option value="$15M – $30M">$15M – $30M</option>
                  <option value="$30M+ (Anchor)">$30M+ (Anchor)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Allocation Objective *</label>
                <textarea name="objective" required rows={4} value={formData.objective} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] resize-none"
                  placeholder="Yield mandate, USD diversification, Africa allocation, strategic asset access..."
                  data-testid="africa-investor-input-objective" />
              </div>

              <button type="submit" disabled={isSubmitting}
                className="w-full bg-[#1a365d] hover:bg-[#142c4d] text-white py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="africa-investor-submit-btn">
                {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>) : (<><Send className="w-5 h-5" /> Send Inquiry</>)}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AfricaInvestorModal;
